import { Prompt } from 'react-router';
import React, { FC, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';

type ConfirmCancelModalProps = {
  customText?: string;
  name: string;
  handleLeave: Function;
  handleStay: Function;
  isModified?: boolean;
  isVisible: boolean;
};

const ConfirmCancelModal: FC<ConfirmCancelModalProps> = ({
  handleLeave,
  handleStay,
  isModified = false,
  isVisible,
  name,
  customText,
}) => {
  const location = useLocation();
  const text = customText || `If you leave this page, your changes will not be applied to ${name}.`;

  const [modalVisible, setModalVisible] = useState(isVisible);
  const [dataIsModified, setDataIsModified] = useState(isModified);
  const [navigateTo, setNavigateTo] = useState('');

  useEffect(() => {
    setModalVisible(isVisible);
    setDataIsModified(isModified);
  }, [isModified, isVisible, setDataIsModified, setModalVisible]);

  const handleConfirmNavigationClick = () => {
    setModalVisible(false);
    handleStay();
  };

  const handleCancel = () => {
    handleLeave(navigateTo);
  };

  const handleBlockedNavigation = useCallback(
    newLocation => {
      setNavigateTo(newLocation);
      if (isModified && newLocation.pathname !== location.pathname) {
        setModalVisible(true);
      }
      return !isModified;
    },
    [isModified, setModalVisible, setNavigateTo, location]
  );

  return (
    <>
      {modalVisible ? (
        <CancelConfirmationModal
          onCancel={handleCancel}
          onSubmit={handleConfirmNavigationClick}
          confirmOnDismiss
          submitText="Stay on Page"
          cancelText="Leave"
          text={text}
          title="You have unsaved changes"
        />
      ) : (
        <Prompt when={dataIsModified} message={handleBlockedNavigation} />
      )}
    </>
  );
};

const CancelConfirmationModal = styled(StyledConfirmOrCancelModal)`
  position: fixed;
  width: 100vh;
  height: 100vh;
  z-index: 100;
`;

export default ConfirmCancelModal;
